/**
 * Talking to the Worker.
 *
 * iOS runs no reliable background task for a PWA, so nothing here is scheduled:
 * the app refreshes when it is opened, and that is all it promises. When the
 * network is unavailable the last snapshot is served from localStorage, and if
 * there has never been one, from the build-time seed. The screen always says
 * which of the three it is showing.
 */
import { GAMES, GAME_KEYS } from '../engine';
import type { GameKey } from '../engine';
import { SEED_DRAWS, SEED_GENERATED_AT } from '../data/seed';

export interface DrawRecord {
  /** YYYY-MM-DD */
  date: string;
  main: number[];
  bonus: number[];
  /** Announced jackpot, in cents. null when unknown. */
  jackpotCents: number | null;
}

export interface DashboardPayload {
  /** Latest known draw per game; null when the history is empty. */
  latest: { [K in GameKey]: DrawRecord | null };
  /** ISO instant the server built this payload. */
  fetchedAt: string;
  /**
   * Games whose expected draw is missing and which the server is fetching right
   * now. Non-empty means: come back in a few seconds and there will be more.
   */
  refreshing: GameKey[];
}

export type Provenance = 'network' | 'cache' | 'seed';

export interface Dashboard extends DashboardPayload {
  provenance: Provenance;
}

const CACHE_KEY = 'lotomatrix.dashboard.v1';
const API_BASE = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '';
const TIMEOUT_MS = 6000;

function seedDashboard(): Dashboard {
  const latest = {} as { [K in GameKey]: DrawRecord | null };
  for (const key of GAME_KEYS) {
    const draws = SEED_DRAWS[key];
    const last = draws.length > 0 ? draws[draws.length - 1] : undefined;
    latest[key] =
      last === undefined
        ? null
        : { date: last.date, main: [...last.main], bonus: [...last.bonus], jackpotCents: null };
  }
  return {
    latest,
    fetchedAt: `${SEED_GENERATED_AT}T00:00:00.000Z`,
    refreshing: [],
    provenance: 'seed',
  };
}

function readCache(): Dashboard | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as DashboardPayload;
    if (typeof parsed.fetchedAt !== 'string' || typeof parsed.latest !== 'object') return null;
    return { ...parsed, refreshing: [], provenance: 'cache' };
  } catch {
    return null;
  }
}

function writeCache(payload: DashboardPayload): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Storage full or blocked: the app works without it.
  }
}

/** Reject anything that does not match the game's matrix, rather than display it. */
function validRecord(key: GameKey, record: unknown): record is DrawRecord {
  if (typeof record !== 'object' || record === null) return false;
  const r = record as Partial<DrawRecord>;
  const g = GAMES[key];
  if (typeof r.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(r.date)) return false;
  if (!Array.isArray(r.main) || r.main.length !== g.k) return false;
  if (!Array.isArray(r.bonus) || r.bonus.length !== g.bk) return false;
  const inRange = (xs: number[], max: number): boolean =>
    xs.every((x) => Number.isInteger(x) && x >= 1 && x <= max) && new Set(xs).size === xs.length;
  return inRange(r.main, g.n) && inRange(r.bonus, g.bmax);
}

/**
 * @param bypassCache set when re-checking after the server said it was fetching a
 *   missing draw, so the browser's own cache cannot serve the stale answer back.
 */
export async function fetchDashboard(bypassCache = false): Promise<Dashboard> {
  const fallback = readCache() ?? seedDashboard();
  if (API_BASE === '') return fallback;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const url = bypassCache
      ? `${API_BASE}/api/dashboard?t=${Date.now()}`
      : `${API_BASE}/api/dashboard`;
    const response = await fetch(url, {
      signal: controller.signal,
      cache: bypassCache ? 'reload' : 'default',
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return fallback;
    const body = (await response.json()) as DashboardPayload;

    const latest = {} as { [K in GameKey]: DrawRecord | null };
    for (const key of GAME_KEYS) {
      const candidate = body.latest?.[key];
      latest[key] = validRecord(key, candidate) ? candidate : (fallback.latest[key] ?? null);
    }
    const payload: DashboardPayload = {
      latest,
      fetchedAt: typeof body.fetchedAt === 'string' ? body.fetchedAt : new Date().toISOString(),
      refreshing: Array.isArray(body.refreshing)
        ? body.refreshing.filter((g): g is GameKey => GAME_KEYS.includes(g as GameKey))
        : [],
    };
    writeCache(payload);
    return { ...payload, provenance: 'network' };
  } catch {
    return fallback;
  } finally {
    clearTimeout(timer);
  }
}

export interface PayoutRow {
  rank: number;
  winners: number | null;
  amountCents: number | null;
}

/**
 * Payout tables for specific draw dates, used by the running total on the Grids
 * screen. Returns an empty map when no server is configured: the snapshot embedded
 * in the build carries draw results, never the amounts paid.
 */
export async function fetchPayouts(
  game: GameKey,
  dates: readonly string[],
): Promise<Record<string, PayoutRow[]>> {
  if (API_BASE === '' || dates.length === 0) return {};

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const query = dates.slice(0, 60).join(',');
    const response = await fetch(`${API_BASE}/api/payouts?game=${game}&dates=${query}`, {
      signal: controller.signal,
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return {};
    const body = (await response.json()) as { payouts?: Record<string, PayoutRow[]> };
    return body.payouts ?? {};
  } catch {
    return {};
  } finally {
    clearTimeout(timer);
  }
}

/** Full history for the Statistics screen: server if configured, seed otherwise. */
export async function fetchHistory(
  game: GameKey,
): Promise<{ draws: DrawRecord[]; provenance: Provenance }> {
  const seed = SEED_DRAWS[game].map<DrawRecord>((d) => ({
    date: d.date,
    main: [...d.main],
    bonus: [...d.bonus],
    jackpotCents: null,
  }));
  if (API_BASE === '') return { draws: seed, provenance: 'seed' };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`${API_BASE}/api/history?game=${game}`, {
      signal: controller.signal,
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return { draws: seed, provenance: 'seed' };
    const body = (await response.json()) as { draws?: unknown };
    const draws = Array.isArray(body.draws)
      ? body.draws.filter((d): d is DrawRecord => validRecord(game, d))
      : [];
    if (draws.length === 0) return { draws: seed, provenance: 'seed' };
    return { draws, provenance: 'network' };
  } catch {
    return { draws: seed, provenance: 'seed' };
  } finally {
    clearTimeout(timer);
  }
}
